import { useRef, useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Upload } from "lucide-react";
import { PhoneInput } from "@/components/site/PhoneInput";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    businessName: "Business name",
    optional: "(optional)",
    name: "Name *",
    phone: "Phone *",
    email: "Email *",
    address: "Address *",
    purchaseDate: "Date of purchase *",
    itemName: "Item name *",
    itemPlaceholder: "e.g. Dexter 5,200 lb axle",
    purchasePrice: "Purchase price *",
    pricePlaceholder: "$",
    receipt: "Receipt *",
    receiptHelp: "(image or PDF, up to 15 MB)",
    fileSelected: (name: string, size: string) => `Selected: ${name} (${size} KB)`,
    errorRequiredReceipt: "Please attach your receipt.",
    errorGeneral: "Something went wrong. Please try again or call the shop.",
    errorNetwork: "Network error. Please try again or call the shop.",
    cancel: "Cancel",
    submitting: "Submitting…",
    submitClaim: "Submit claim",
    title: "Redeem your axle rebate",
    description: "Fill out the form and attach your receipt. We'll process the claim and email you a confirmation.",
    successTitle: "Claim submitted",
    successDesc: "We received your rebate claim and emailed it to our accounting team. You'll hear back within a few business days. Questions in the meantime - call the shop at (574) 264-9434.",
    done: "Done"
  },
  es: {
    businessName: "Nombre del negocio",
    optional: "(opcional)",
    name: "Nombre *",
    phone: "Teléfono *",
    email: "Correo electrónico *",
    address: "Dirección *",
    purchaseDate: "Fecha de compra *",
    itemName: "Nombre del artículo *",
    itemPlaceholder: "ej. Eje Dexter de 5,200 lb",
    purchasePrice: "Precio de compra *",
    pricePlaceholder: "$",
    receipt: "Recibo *",
    receiptHelp: "(imagen o PDF, hasta 15 MB)",
    fileSelected: (name: string, size: string) => `Seleccionado: ${name} (${size} KB)`,
    errorRequiredReceipt: "Por favor, adjunte su recibo.",
    errorGeneral: "Ocurrió un error. Por favor, intente de nuevo o llame al taller.",
    errorNetwork: "Error de red. Por favor, intente de nuevo o llame al taller.",
    cancel: "Cancelar",
    submitting: "Enviando…",
    submitClaim: "Enviar reclamo",
    title: "Canjear su reembolso de eje",
    description: "Complete el formulario y adjunte su recibo. Procesaremos el reclamo y le enviaremos una confirmación por correo electrónico.",
    successTitle: "Reclamo enviado",
    successDesc: "Recibimos su reclamo de reembolso y lo enviamos a nuestro equipo de contabilidad. Recibirá noticias en unos días hábiles. Si tiene preguntas, llame al taller al (574) 264-9434.",
    done: "Listo"
  }
};

type FieldKey =
  | "businessName"
  | "name"
  | "address"
  | "phone"
  | "email"
  | "purchaseDate"
  | "itemName"
  | "purchasePrice";

const INITIAL: Record<FieldKey, string> = {
  businessName: "",
  name: "",
  address: "",
  phone: "",
  email: "",
  purchaseDate: "",
  itemName: "",
  purchasePrice: "",
};

export function RebateRedeemDialog({ children }: { children: ReactNode }) {
  const t = useCopy(COPY);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(INITIAL);
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  function update<K extends FieldKey>(key: K, v: string) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  function reset() {
    setValues(INITIAL);
    setFile(null);
    setSubmitted(false);
    setError(null);
    setSending(false);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError(t.errorRequiredReceipt);
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(values)) fd.append(k, v);
      fd.append("receipt", file);
      const res = await fetch("/api/rebate-claim", { method: "POST", body: fd });
      const data = (await res.json().catch(() => ({}))) as { error?: string; ok?: boolean };
      if (!res.ok || !data.ok) {
        setError(data.error || t.errorGeneral);
        setSending(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setError(t.errorNetwork);
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-xl w-[95vw] max-h-[92vh] overflow-y-auto">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>{t.title}</DialogTitle>
              <DialogDescription>
                {t.description}
              </DialogDescription>
            </DialogHeader>
            <form ref={formRef} onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="businessName">{t.businessName} <span className="text-muted-foreground font-normal">{t.optional}</span></Label>
                <Input id="businessName" value={values.businessName} onChange={(e) => update("businessName", e.target.value)} autoComplete="organization" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t.name}</Label>
                  <Input id="name" required value={values.name} onChange={(e) => update("name", e.target.value)} autoComplete="name" />
                </div>
                <div>
                  <Label htmlFor="phone">{t.phone}</Label>
                  <PhoneInput id="phone" required value={values.phone} onValueChange={(phone) => update("phone", phone)} autoComplete="tel" />
                </div>
              </div>
              <div>
                <Label htmlFor="email">{t.email}</Label>
                <Input id="email" type="email" required value={values.email} onChange={(e) => update("email", e.target.value)} autoComplete="email" />
              </div>
              <div>
                <Label htmlFor="address">{t.address}</Label>
                <Textarea id="address" required value={values.address} onChange={(e) => update("address", e.target.value)} rows={2} autoComplete="street-address" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="purchaseDate">{t.purchaseDate}</Label>
                  <Input id="purchaseDate" type="date" required value={values.purchaseDate} onChange={(e) => update("purchaseDate", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="itemName">{t.itemName}</Label>
                  <Input id="itemName" required value={values.itemName} onChange={(e) => update("itemName", e.target.value)} placeholder={t.itemPlaceholder} />
                </div>
              </div>
              <div>
                <Label htmlFor="purchasePrice">{t.purchasePrice}</Label>
                <Input id="purchasePrice" required value={values.purchasePrice} onChange={(e) => update("purchasePrice", e.target.value)} placeholder={t.pricePlaceholder} inputMode="decimal" />
              </div>
              <div>
                <Label htmlFor="receipt">{t.receipt} <span className="text-muted-foreground font-normal">{t.receiptHelp}</span></Label>
                <div className="mt-1 flex items-center gap-3">
                  <Input
                    id="receipt"
                    type="file"
                    required
                    accept="image/*,application/pdf"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="file:mr-3 file:rounded file:border-0 file:bg-secondary/10 file:px-3 file:py-1 file:text-sm file:font-medium"
                  />
                </div>
                {file && <p className="mt-1 text-xs text-muted-foreground">{t.fileSelected(file.name, (file.size / 1024).toFixed(1))}</p>}
              </div>

              {error && (
                <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-primary">{error}</div>
              )}

              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={sending}>{t.cancel}</Button>
                <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90" disabled={sending}>
                  {sending ? t.submitting : <><Upload className="mr-1.5 h-4 w-4" /> {t.submitClaim}</>}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="py-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600" /> {t.successTitle}</DialogTitle>
              <DialogDescription>
                {t.successDesc}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setOpen(false)} className="bg-primary text-primary-foreground hover:bg-primary/90">{t.done}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
